/**
 * ⭐ Page guides (owner, 2026-10-10; wireframe Shared/Claude-Codex/009-page-guides) —
 * pure helpers.
 *
 * A guide is a short sequence (3–5 steps) for ONE page. It dims the app and
 * spotlights one thing at a time with a card. It runs by itself on a person's
 * first visit and can be replayed from "?" in the side navigation.
 *
 * ⚠️ Rules:
 *  · an ACTION step has no Next — it moves on when the person clicks the spotlighted
 *    thing (`'target-click'`) or changes a value in it (`'target-change'`, e.g. a
 *    slider), so they learn by doing it;
 *  · an OPTIONAL step is skipped when its target is not on the page (e.g. a tab that
 *    is still locked);
 *  · a page can hold several guides (CHAPTERS): the overview, and one per tab that
 *    runs the first time that tab opens. `level` orders them — "?" replays the
 *    deepest one on screen; on a first visit the shallower one runs first;
 *  · a step whose target is not on screen shows centred, without a spotlight —
 *    never a crash or a card pointing at nothing;
 *  · "seen" is per account and per VERSION: rewriting a guide (a higher version)
 *    lets it show once more;
 *  · ⭐ (2026-10-11) a long guide walks a whole page END TO END: each step can name
 *    a SCENE the page sets up before the step shows (open a domain, switch a tab),
 *    a SECTION it belongs to (the card's Steps list groups by it, and the person can
 *    jump to any step), and a MISSING BODY said instead when its target isn't there
 *    (a tab still locked) — rather than a card pointing at nothing.
 */

export interface GuideStep {
  id: string;
  /** The `data-guide` value of the element to spotlight; null = a centred card. */
  target: string | null;
  title: string;
  body: string;
  /** Moves on when the spotlighted element is clicked / has a value changed (no Next). */
  advanceOn?: 'target-click' | 'target-change';
  /** Skipped when its target is not on the page. */
  optional?: boolean;
  /** Shown on an action step while it waits ("Waiting for you to pick one"). */
  waitingText?: string;
  /** A short list under the body — one line each, the label in bold. */
  items?: { label: string; text: string }[];
  /** The Steps list heading this step sits under ("Assess"). */
  section?: string;
  /** What the page sets up before this step shows — the page decides what it means. */
  scene?: string;
  /** Said instead of `body` while the target is not on the page (an info step: Next moves on). */
  missingBody?: string;
}

export interface PageGuide {
  /** A storage key (per-account "seen") — never rename. */
  id: string;
  version: number;
  /** Plain name, for the "?" tooltip and announcements ("Life Map guide"). */
  name: string;
  /** 0 = the page's overview; 1 = a chapter inside it (a tab). Default 0. */
  level?: number;
  steps: GuideStep[];
}

/**
 * The chapter that follows a finished guide: the deepest UNSEEN guide on screen
 * that is deeper than it (the open tab's, after the page overview). null = none.
 */
export function nextChapter(stack: PageGuide[], after: PageGuide, seen: Record<string, number> | undefined): PageGuide | null {
  const level = after.level ?? 0;
  const deeper = stack.filter(g => g.id !== after.id && (g.level ?? 0) > level && shouldAutoStart(g, seen));
  return deeper.length ? deeper[deeper.length - 1] : null;
}

/** Should this guide show by itself? Only while unseen at its current version. */
export function shouldAutoStart(guide: PageGuide, seen: Record<string, number> | undefined): boolean {
  return (seen?.[guide.id] ?? 0) < guide.version;
}

export const guideTarget = (id: string) => `[data-guide="${id}"]`;

/** The Steps list: consecutive steps grouped under their section (untitled → ''). */
export function stepSections(guide: PageGuide): { section: string; steps: { index: number; step: GuideStep }[] }[] {
  const out: { section: string; steps: { index: number; step: GuideStep }[] }[] = [];
  guide.steps.forEach((step, index) => {
    const section = step.section ?? '';
    const last = out[out.length - 1];
    if (last && last.section === section) last.steps.push({ index, step });
    else out.push({ section, steps: [{ index, step }] });
  });
  return out;
}

/**
 * "Seen" key for the closing pointer ("replay any time from Guide"): it always
 * follows the overview's Done; a chapter's Done or any Skip only until seen once.
 */
export const REPLAY_POINTER_ID = '_replay-pointer';
/** Where the pointer points: the rail's Guide button, else (a phone) the menu button. */
export const REPLAY_TARGETS = ['nav.guide', 'nav.menu'] as const;

export interface Rect { left: number; top: number; width: number; height: number }
export interface CardPlacement { left: number; top: number; side: 'below' | 'above' | 'right' | 'left' | 'centre' }

const GAP = 12;
const EDGE = 12;

/**
 * Where the card goes, in viewport coordinates: below the target if it fits, else
 * above, else to its right, else its left — NEVER over the target when there is any
 * room. With no target (or no room anywhere) it is centred.
 */
export function placeCard(target: Rect | null, card: { width: number; height: number }, view: { width: number; height: number }): CardPlacement {
  const centre = (): CardPlacement => ({
    left: Math.max(EDGE, (view.width - card.width) / 2),
    top: Math.max(EDGE, (view.height - card.height) / 2),
    side: 'centre',
  });
  if (!target) return centre();
  const clampX = (x: number) => Math.max(EDGE, Math.min(x, view.width - card.width - EDGE));
  const clampY = (y: number) => Math.max(EDGE, Math.min(y, view.height - card.height - EDGE));
  const midX = target.left + target.width / 2 - card.width / 2;
  const midY = target.top + target.height / 2 - card.height / 2;
  const below = target.top + target.height + GAP;
  if (below + card.height <= view.height - EDGE) return { left: clampX(midX), top: below, side: 'below' };
  const above = target.top - GAP - card.height;
  if (above >= EDGE) return { left: clampX(midX), top: above, side: 'above' };
  const right = target.left + target.width + GAP;
  if (right + card.width <= view.width - EDGE) return { left: right, top: clampY(midY), side: 'right' };
  const left = target.left - GAP - card.width;
  if (left >= EDGE) return { left, top: clampY(midY), side: 'left' };
  return centre();
}

/**
 * ⭐ Where a PHONE docks the card (2026-10-16): at the bottom — unless the top covers
 * LESS of the target. A target at the very end of a page (the Roadmap's action row)
 * cannot be scrolled above a bottom card, and sat hidden under it. A target that
 * fills the screen is covered the same either way, and keeps the card at the bottom.
 */
export function phoneDock(spot: Rect | null, cardHeight: number, view: { height: number }): 'top' | 'bottom' {
  if (!spot) return 'bottom';
  const underBottom = Math.max(0, spot.top + spot.height - (view.height - EDGE - cardHeight));
  const underTop = Math.max(0, EDGE + cardHeight - spot.top);
  return underTop < underBottom ? 'top' : 'bottom';
}

/** The spotlight hole: the target, padded, clipped to the viewport (null when off screen). */
export function spotlightRect(target: Rect | null, view: { width: number; height: number }, pad = 6): Rect | null {
  if (!target) return null;
  const left = Math.max(0, target.left - pad);
  const top = Math.max(0, target.top - pad);
  const right = Math.min(view.width, target.left + target.width + pad);
  const bottom = Math.min(view.height, target.top + target.height + pad);
  if (right <= left || bottom <= top) return null;
  return { left, top, width: right - left, height: bottom - top };
}

/**
 * The brand ring around the spotlight: 4px outside it, but kept 1px inside the
 * screen — a target at the very edge (the phone's menu button) keeps all four sides.
 */
export function ringRect(spot: Rect, view: { width: number; height: number }, gap = 4): Rect {
  const left = Math.max(1, spot.left - gap);
  const top = Math.max(1, spot.top - gap);
  const right = Math.min(view.width - 1, spot.left + spot.width + gap);
  const bottom = Math.min(view.height - 1, spot.top + spot.height + gap);
  return { left, top, width: right - left, height: bottom - top };
}
